import { recordAnswerAction } from 'api/api'
import toast from 'utils/toast'
import {
    addMediaTrackToPlaylist,
    getMediaTrack,
    isMediaTrackInPlaylist,
    removeMediaTrackFromPlaylist,
    requestMediaDockCinema,
    requestMediaDockHistoryFocus,
    requestMediaDockPlay,
    requestMediaDockPlayInDock,
    requestMediaDockPlaylistFocus,
    requestMediaDockRadioFocus,
    requestMediaDockRadioStop,
} from 'state/mediaPlaybackCoordinator'

// Amplitude wrapper — never let telemetry break playback.
export function logMediaEvent(event, props = {}) {
    try {
        window.logEv?.(event, props)
    } catch {}
}

function trackProps(track) {
    return {
        media_key: track?.mediaKey || null,
        kind: track?.kind || null,
        answer_id: track?.answerId || null,
    }
}

// Records a play both in Amplitude and as an answer Action (indexable).
export function recordMediaPlay(mediaKey, source) {
    const track = getMediaTrack(mediaKey)
    if (!track) return

    logMediaEvent('MEDIA_PLAY', { ...trackProps(track), source })
    if (track.answerId) {
        recordAnswerAction(track.answerId, 'media_play', {
            media_key: track.mediaKey,
            kind: track.kind || null,
            source,
        })
    }
}

// Adds to the dock playlist with Amplitude + answer Action telemetry.
// Returns whether the track was newly added. Adding never opens the dock: a
// toast confirms it and its Open action shows the playlist. Callers already
// inside the dock pass notify: false.
export function addTrackToPlaylist(mediaKey, source, { notify = true } = {}) {
    const track = getMediaTrack(mediaKey)
    if (!track) return false

    const added = addMediaTrackToPlaylist(mediaKey, {
        position: 'next',
        moveExisting: true,
    })

    if (!added) return false

    if (notify) {
        toast.success('Added to playlist', {
            kind: 'media-dock-playlist-add',
            testId: 'media-dock-playlist-add-toast',
            actionLabel: 'Open',
            cancelLabel: 'Dismiss',
            onAction: () => requestMediaDockPlaylistFocus({ mediaKey: track.mediaKey, source }),
        })
    }

    logMediaEvent('MEDIA_DOCK_ADD_TO_PLAYLIST', { ...trackProps(track), source })
    if (track?.answerId) {
        recordAnswerAction(track.answerId, 'add_to_playlist', {
            media_key: track.mediaKey,
            kind: track.kind || null,
            source,
        })
    }
    return true
}

// Takes a track out of the dock playlist from a surface outside the dock.
// Returns whether it was in the playlist.
export function removeTrackFromPlaylist(mediaKey, source) {
    if (!isMediaTrackInPlaylist(mediaKey)) return false
    const track = getMediaTrack(mediaKey)
    removeMediaTrackFromPlaylist(mediaKey)
    logMediaEvent('MEDIA_DOCK_REMOVE_FROM_PLAYLIST', {
        ...trackProps(track),
        media_key: track?.mediaKey || mediaKey,
        source,
    })
    return true
}

// A media dropdown's playlist row: adds the track, or removes it when it is
// already in the playlist (the row's label says which, via playlistMenuLabel).
export function toggleTrackInPlaylist(mediaKey, source) {
    if (isMediaTrackInPlaylist(mediaKey)) {
        removeTrackFromPlaylist(mediaKey, source)
        return
    }
    addTrackToPlaylist(mediaKey, source)
}

export function openMediaDockPlaylist(source) {
    logMediaEvent('MEDIA_DOCK_OPEN_PLAYLIST', { source })
    requestMediaDockPlaylistFocus({ source })
}

export function openMediaDockRadio(source) {
    logMediaEvent('MEDIA_DOCK_OPEN_RADIO', { source })
    requestMediaDockRadioFocus({ source })
}

// Leave the broadcast from a surface outside the dock (the ON AIR card's
// tuner). The station keeps playing; only this listener goes quiet.
export function stopMediaDockRadio(source) {
    logMediaEvent('MEDIA_DOCK_TUNE_OUT', { source })
    requestMediaDockRadioStop({ source })
}

// Play a registered track through the dock from a surface that has no player
// of its own (the "Up next" race card). Returns false when nothing registered
// the key yet.
export function playMediaDockTrack(mediaKey, source) {
    const started = requestMediaDockPlay({ mediaKey, source })
    if (started) logMediaEvent('MEDIA_DOCK_CARD_PLAY', { media_key: mediaKey, source })
    return started
}

// Play a track and show it in the dock's History tab — where a finished radio
// play belongs once the station has moved on.
export function playMediaDockTrackInHistory(mediaKey, source) {
    const started = requestMediaDockPlay({ mediaKey, source })
    requestMediaDockHistoryFocus({ mediaKey, source })
    if (started) logMediaEvent('MEDIA_DOCK_CARD_PLAY', { media_key: mediaKey, source })
    return started
}

// A media dropdown's "Play in Dock" row: move the track's playback into the
// dock. Returns false when nothing registered the key.
export function playTrackInDock(mediaKey, source) {
    return requestMediaDockPlayInDock({ mediaKey, source })
}

// Watch a registered track on the dock's cinema stage — the app window, not
// the browser's fullscreen — from a media dropdown. The dock logs the entry
// once it actually happens. Returns false when nothing registered the key.
export function openTrackInCinema(mediaKey, source, options = {}) {
    return requestMediaDockCinema({ mediaKey, source, ...options })
}
